import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import { AlertVariant } from '../../../../constants/constants';
import { _ToastMessageProps } from '../../../../constants/staticTypes';
import MlxDialog from '../../../../components/common/MlxDialog/MlxDialog';
import { getOtpForEmailUpdateOrgStaff, updateOrgStaffEmailId } from '../../../../helpers/authHelper';
import { useMutation } from 'react-query';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';

interface ChangeEmailModalProps {
    visible: boolean;
    onHide: () => void;
    userData: any;
    setToastMessage: (message: _ToastMessageProps) => void;
}

const languageTexts = {
    CHANGE_EMAIL: 'Change Email',
    CURRENT_EMAIL: 'Current Email',
    GET_OTP: 'Get OTP',
    ENTER_OTP: 'Enter OTP',
    NEW_EMAIL: 'New Email',
    SUBMIT: 'Submit',
};

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({ visible, onHide, userData, setToastMessage }) => {
    const getOtpToEmailIdMutation = useMutation(HttpServiceHelper);
    const updateEmailIdMutation = useMutation(HttpServiceHelper);
    const [currentEmail, setCurrentEmail] = useState<string>('');
    const [isOtpLoading, setIsOtpLoading] = useState<boolean>(false);
    const [otpCode, setOtpCode] = useState<string>('');
    const [newEmail, setNewEmail] = useState<string>('');
    const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<string>('');

    useEffect(() => {
        if (visible) {
            setCurrentEmail(userData?.email || '');
        }
    }, [visible, userData]);

    const handleCloseModal = () => {
        setCurrentEmail(userData?.email || '');
        setOtpCode('');
        setNewEmail('');
        setIsOtpSent(false);
        setEmailError('');
        onHide();
    };

    const validateEmailFormat = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email.trim() && !emailRegex.test(email.trim())) {
            setEmailError('Please enter a valid email format');
        } else {
            setEmailError('');
        }
    };

    const handleGetOtp = async () => {
        if (!currentEmail.trim()) {
            setToastMessage({
                message: 'Please enter a valid email address',
                variant: AlertVariant.ERROR,
                show: true,
            });
            return;
        }

        setIsOtpLoading(true);
        try {
            const onSuccess = () => {
                setToastMessage({
                    message: 'OTP sent successfully to your email',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
            };
            const onError = (error: any) => {
                setToastMessage({
                    message: error?.message || 'Failed to send OTP. Please try again.',
                    variant: AlertVariant.ERROR,
                    show: true,
                });
            };
            getOtpForEmailUpdateOrgStaff(getOtpToEmailIdMutation, { email: currentEmail, staffId: userData.id }, onSuccess, onError);
            setIsOtpSent(true);
        } catch (error) {
            setToastMessage({
                message: 'Failed to send OTP. Please try again.',
                variant: AlertVariant.ERROR,
                show: true,
            });
        } finally {
            setIsOtpLoading(false);
        }
    };

    const handleSubmitEmailChange = async () => {
        if (!otpCode.trim()) {
            setToastMessage({
                message: 'Please enter the OTP code',
                variant: AlertVariant.ERROR,
                show: true,
            });
            return;
        }

        if (!newEmail.trim()) {
            setToastMessage({
                message: 'Please enter a new email address',
                variant: AlertVariant.ERROR,
                show: true,
            });
            return;
        }

        // Check if there's an email format error
        if (emailError) {
            return;
        }

        try {
            const onSuccess = () => {
                setToastMessage({
                    message: 'Email changed successfully',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                window.location.reload(); // Reload to reflect changes
            };
            const OnError = (error: any) => {
                setToastMessage({
                    message: error?.message || 'Failed to change email. Please try again.',
                    variant: AlertVariant.ERROR,
                    show: true,
                });
            };

            updateOrgStaffEmailId(
                updateEmailIdMutation,
                {
                    staffId: userData.id,
                    oldEmail: currentEmail,
                    newEmail,
                    code: otpCode,
                },
                onSuccess,
                OnError
            );
            handleCloseModal();
        } catch (error) {
            setToastMessage({
                message: 'Failed to change email. Please try again.',
                variant: AlertVariant.ERROR,
                show: true,
            });
        }
    };

    return (
        <MlxDialog
            header={languageTexts.CHANGE_EMAIL}
            visible={visible}
            onHide={handleCloseModal}
            style={{ width: '530px' }}
            modal
            resizable={false}
            draggable={false}
            className="email-change-dialog"
        >
            <div className="email-dialog-content">
                <div className="mb-3">
                    <label className="mb-2 d-block" htmlFor="currentEmailDialog">
                        {languageTexts.CURRENT_EMAIL}
                    </label>
                    <InputText
                        id="currentEmailDialog"
                        value={currentEmail}
                        onChange={(e) => setCurrentEmail(e.target.value)}
                        className="w-100 custom-input-text"
                        placeholder="Enter your email address"
                        type="email"
                        disabled={isOtpSent}
                    />
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <MlxButton
                        label={languageTexts.GET_OTP}
                        icon="ri-mail-send-line"
                        iconPos="right"
                        size="small"
                        onClick={handleGetOtp}
                        loading={isOtpLoading}
                        disabled={isOtpLoading || !currentEmail.trim() || isOtpSent}
                    />
                </div>

                {isOtpSent && (
                    <>
                        <div className="mb-3">
                            <label className="mb-2 d-block" htmlFor="otpCodeDialog">
                                {languageTexts.ENTER_OTP}
                            </label>
                            <InputText
                                id="otpCodeDialog"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value)}
                                className="w-100 custom-input-text"
                                placeholder="Enter OTP code"
                                type="text"
                                maxLength={6}
                                autoComplete="off"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 d-block" htmlFor="newEmailDialog">
                                {languageTexts.NEW_EMAIL}
                            </label>
                            <InputText
                                id="newEmailDialog"
                                value={newEmail}
                                onChange={(e) => {
                                    setNewEmail(e.target.value);
                                    validateEmailFormat(e.target.value);
                                }}
                                className="w-100 custom-input-text"
                                placeholder="Enter new email address"
                                type="email"
                                autoComplete="off"
                            />
                            {emailError && <small className="text-danger mt-1 d-block">{emailError}</small>}
                        </div>

                        <div className="d-flex justify-content-end">
                            <MlxButton
                                label={languageTexts.SUBMIT}
                                icon="ri-check-line"
                                iconPos="right"
                                size="small"
                                onClick={handleSubmitEmailChange}
                                disabled={!otpCode.trim() || !newEmail.trim() || !!emailError}
                            />
                        </div>
                    </>
                )}
            </div>
        </MlxDialog>
    );
};

export default ChangeEmailModal;
